import { Platform, Share } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';

import { UTF8_BOM } from '../domain/csvConstants';
import { sanitizeCsvFileName } from '../domain/csvFileName';
import {
  markUnexpectedFailureHandled,
} from '../../sentry/presentUnexpectedFailure';
import { reportUserVisibleFailure } from '../../sentry/sentryUser';

export type CsvShareOutcome = 'ok' | 'cancelled';

/**
 * Reporta a Sentry (`user_visible`) sin toast: los callers ya muestran error.
 * Cancel/dismiss → no report. El Error relanzado lleva marca para no doble-report.
 */
function reportShareFailureAndThrow(error: unknown, userMessage: string): never {
  try {
    reportUserVisibleFailure({
      module: 'csv',
      op: 'share_or_download',
      error,
      title: 'Exportar CSV',
      detail: userMessage,
    });
  } catch {
    // Reporting must never replace the share failure path.
  }
  throw markUnexpectedFailureHandled(new Error(userMessage));
}

export async function shareOrDownloadCsv(csv: string, rawFilename: string): Promise<CsvShareOutcome> {
  const filename = sanitizeCsvFileName(rawFilename);
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    try {
      const blob = new Blob([`${UTF8_BOM}${csv}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
      return 'ok';
    } catch (e) {
      reportShareFailureAndThrow(e, 'No se pudo iniciar la descarga en el navegador.');
    }
  }

  // Native: escribir a archivo temporal y compartir como archivo real (sin límite de tamaño).
  try {
    const uri = `${FileSystem.cacheDirectory}${filename}`;
    await FileSystem.writeAsStringAsync(uri, `${UTF8_BOM}${csv}`, {
      encoding: FileSystem.EncodingType.UTF8,
    });
    const canShare = await Sharing.isAvailableAsync();
    if (!canShare) {
      // Fallback: Share.share con texto (sujeto a límites del OS).
      const result = await Share.share({
        title: filename,
        message:
          csv.length > 180_000
            ? `${csv.slice(0, 180_000)}\n… [recortado por límite de compartir]`
            : csv,
      });
      if (result.action === Share.dismissedAction) return 'cancelled';
      return 'ok';
    }
    await Sharing.shareAsync(uri, {
      mimeType: 'text/csv',
      dialogTitle: filename,
      UTI: 'public.comma-separated-values-text',
    });
    return 'ok';
  } catch (e) {
    if (e instanceof Error && /cancel/i.test(e.message)) return 'cancelled';
    reportShareFailureAndThrow(e, 'No se pudo compartir el archivo CSV.');
  }
}

/**
 * TXT plano (sin BOM — los archivos ARCA son ASCII puro).
 * Usado por el export Libro IVA Digital.
 */
export async function shareOrDownloadTxt(content: string, rawFilename: string): Promise<CsvShareOutcome> {
  const filename = sanitizeCsvFileName(rawFilename);
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    try {
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
      return 'ok';
    } catch (e) {
      reportShareFailureAndThrow(e, 'No se pudo iniciar la descarga en el navegador.');
    }
  }

  try {
    const uri = `${FileSystem.cacheDirectory}${filename}`;
    await FileSystem.writeAsStringAsync(uri, content, {
      encoding: FileSystem.EncodingType.UTF8,
    });
    const canShare = await Sharing.isAvailableAsync();
    if (!canShare) {
      const result = await Share.share({
        title: filename,
        message:
          content.length > 180_000
            ? `${content.slice(0, 180_000)}\n… [recortado por límite de compartir]`
            : content,
      });
      if (result.action === Share.dismissedAction) return 'cancelled';
      return 'ok';
    }
    await Sharing.shareAsync(uri, {
      mimeType: 'text/plain',
      dialogTitle: filename,
      UTI: 'public.plain-text',
    });
    return 'ok';
  } catch (e) {
    if (e instanceof Error && /cancel/i.test(e.message)) return 'cancelled';
    reportShareFailureAndThrow(e, 'No se pudo compartir el archivo TXT.');
  }
}
